import { z } from 'zod';
import { ORG_ROLES, PLATFORM_ROLES } from '../permissions';
import { phoneNumberSchema } from './alerts';
import { languageSchema, objectIdSchema } from './common';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MIN_SCORE = 3;

export const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .email()
  .max(254);
/** Length is checked here; strength (zxcvbn) and "not the email" are checked by the server. */
export const newPasswordSchema = z.string().min(PASSWORD_MIN_LENGTH).max(200);
export const orgRoleSchema = z.enum(ORG_ROLES);
export const platformRoleSchema = z.enum(PLATFORM_ROLES);
export const themeSchema = z.enum(['light', 'dark', 'system']);

export const userPreferencesSchema = z.object({
  language: languageSchema.optional(),
  theme: themeSchema.default('system'),
  unitSystemId: z.string().max(64).nullable().optional(),
  unitOverrides: z.record(z.string().max(64), z.string().max(64)).default({}),
  timeZone: z.string().max(64).nullable().optional(),
  defaultOrgId: objectIdSchema.nullable().optional(),
});
export type UserPreferences = z.infer<typeof userPreferencesSchema>;

export const sessionUserSchema = z.object({
  id: objectIdSchema,
  email: z.string(),
  name: z.string(),
  platformRole: platformRoleSchema.nullable(),
  mfaEnabled: z.boolean(),
  preferences: userPreferencesSchema,
  /** The account may only change its password / set up MFA until these are cleared. */
  mustChangePassword: z.boolean(),
  mfaSetupRequired: z.boolean(),
  /** WhatsApp number (E.164) used to reach the person as a well supervisor (doc 14). */
  phone: z.string().nullable().default(null),
});
export type SessionUser = z.infer<typeof sessionUserSchema>;

export const membershipSummarySchema = z.object({
  orgId: objectIdSchema,
  orgName: z.string(),
  orgSlug: z.string(),
  orgStatus: z.enum(['active', 'suspended', 'deleted']),
  role: orgRoleSchema,
  status: z.enum(['invited', 'active', 'suspended']),
});
export type MembershipSummary = z.infer<typeof membershipSummarySchema>;

export const loginRequestSchema = z.object({
  email: emailSchema,
  password: z.string().min(1).max(200),
});
export type LoginRequest = z.infer<typeof loginRequestSchema>;

export const authenticatedResponseSchema = z.object({
  accessToken: z.string(),
  user: sessionUserSchema,
  memberships: z.array(membershipSummarySchema),
  /** Only for a client that cannot keep a cookie (the mobile app): the refresh token itself. */
  refreshToken: z.string().optional(),
});
export type AuthenticatedResponse = z.infer<typeof authenticatedResponseSchema>;

export const mfaChallengeResponseSchema = z.object({
  mfaRequired: z.literal(true),
  mfaToken: z.string(),
});
export const loginResponseSchema = z.union([authenticatedResponseSchema, mfaChallengeResponseSchema]);
export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const mfaVerifyRequestSchema = z
  .object({
    mfaToken: z.string().min(1),
    code: z.string().regex(/^\d{6}$/).optional(),
    recoveryCode: z.string().min(8).max(32).optional(),
  })
  .refine((v) => !!v.code !== !!v.recoveryCode, 'Provide either code or recoveryCode');
export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequestSchema>;

/** The web app sends nothing (its refresh token is a cookie); the mobile app sends its token. */
export const refreshRequestSchema = z.object({ refreshToken: z.string().min(20).max(200).optional() }).nullish();
export const refreshResponseSchema = z.object({ accessToken: z.string(), refreshToken: z.string().optional() });

// ---- Mobile app (doc 12) ----
/** Sent by the mobile app on every request: `x-tl-client: mobile`. */
export const MOBILE_CLIENT_HEADER = 'x-tl-client';
/** Optional name of the phone, shown in the list of sessions. */
export const MOBILE_DEVICE_HEADER = 'x-tl-device';
export const MOBILE_PAIRING_TTL_SEC = 120;
const PAIRING_PREFIX = 'twinlink://pair?';

/** What the QR code of the profile page contains: where the server is, and a one-time code. */
export function buildMobilePairing(server: string, token: string): string {
  return `${PAIRING_PREFIX}s=${encodeURIComponent(server)}&t=${encodeURIComponent(token)}`;
}
export function parseMobilePairing(text: string): { server: string; token: string } | null {
  if (!text.startsWith(PAIRING_PREFIX)) return null;
  const params = new Map(
    text
      .slice(PAIRING_PREFIX.length)
      .split('&')
      .map((part) => {
        const eq = part.indexOf('=');
        return [part.slice(0, eq), part.slice(eq + 1)] as const;
      }),
  );
  try {
    const server = decodeURIComponent(params.get('s') ?? '').replace(/\/+$/, '');
    const token = decodeURIComponent(params.get('t') ?? '');
    if (!/^https?:\/\/[^\s/]+/.test(server) || token.length < 20 || token.length > 200) return null;
    return { server, token };
  } catch {
    return null;
  }
}

export const mobilePairingResponseSchema = z.object({
  /** The text of the QR code. */
  payload: z.string(),
  qrDataUrl: z.string(),
  expiresAt: z.string(),
});
export type MobilePairingResponse = z.infer<typeof mobilePairingResponseSchema>;
export const mobilePairRequestSchema = z.object({ token: z.string().min(20).max(200) });
export type MobilePairRequest = z.infer<typeof mobilePairRequestSchema>;
export const okResponseSchema = z.object({ ok: z.literal(true) });
export type OkResponse = z.infer<typeof okResponseSchema>;

export const forgotPasswordRequestSchema = z.object({ email: emailSchema });
export const resetPasswordRequestSchema = z.object({
  token: z.string().min(20).max(200),
  password: newPasswordSchema,
});

export const invitationInfoSchema = z.object({
  orgName: z.string(),
  email: z.string(),
  role: orgRoleSchema,
  existingUser: z.boolean(),
  expiresAt: z.string(),
});
export type InvitationInfo = z.infer<typeof invitationInfoSchema>;

export const acceptInvitationRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  password: newPasswordSchema.optional(),
});
export const acceptInvitationResponseSchema = z.object({
  orgId: objectIdSchema,
  /** Present when a new account was created and signed in. */
  session: authenticatedResponseSchema.nullable(),
});
export type AcceptInvitationResponse = z.infer<typeof acceptInvitationResponseSchema>;

export const impersonationInfoSchema = z.object({
  orgId: objectIdSchema,
  targetUserId: objectIdSchema,
  targetName: z.string(),
  byName: z.string(),
});

export const meResponseSchema = z.object({
  user: sessionUserSchema,
  memberships: z.array(membershipSummarySchema),
  platformPermissions: z.array(z.string()),
  impersonation: impersonationInfoSchema.nullable(),
});
export type MeResponse = z.infer<typeof meResponseSchema>;

export const updateMeRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  preferences: userPreferencesSchema.partial().optional(),
  /** null removes it. */
  phone: phoneNumberSchema.nullable().optional(),
});
export type UpdateMeRequest = z.infer<typeof updateMeRequestSchema>;

export const changePasswordRequestSchema = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword: newPasswordSchema,
});

export const mfaSetupResponseSchema = z.object({
  secret: z.string(),
  otpauthUrl: z.string(),
  qrDataUrl: z.string(),
});
export type MfaSetupResponse = z.infer<typeof mfaSetupResponseSchema>;
export const mfaEnableRequestSchema = z.object({ code: z.string().regex(/^\d{6}$/) });
export const mfaEnableResponseSchema = z.object({ recoveryCodes: z.array(z.string()) });
export const mfaDisableRequestSchema = z.object({
  password: z.string().min(1).max(200),
  code: z.string().regex(/^\d{6}$/),
});

export const sessionInfoSchema = z.object({
  id: objectIdSchema,
  device: z.string(),
  ip: z.string(),
  userAgent: z.string(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  current: z.boolean(),
});
export type SessionInfo = z.infer<typeof sessionInfoSchema>;
